import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminContracts() {
  const [stats, setStats] = useState(null);
  const [recentContracts, setRecentContracts] = useState([]);
  const [problems, setProblems] = useState(null);
  const [uploadVolume, setUploadVolume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const [statsRes, recentRes, problemsRes, volumeRes] = await Promise.all([
        axios.get(`${API}/admin/contracts/stats?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/contracts/recent?limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/contracts/problems`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/contracts/upload-volume?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setStats(statsRes.data);
      setRecentContracts(recentRes.data.contracts);
      setProblems(problemsRes.data);
      setUploadVolume(volumeRes.data);
    } catch (error) {
      toast.error("Failed to load contract data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading contract data...</p>
        </div>
      </div>
    );
  }

  const maxUploads = Math.max(...(uploadVolume?.timeline || []).map(t => t.uploads), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Contracts / Documents</h1>
          <p className="text-muted-foreground mt-1">OCR monitoring and document management</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="60">Last 60 days</option>
        </select>
      </div>

      {/* Stats KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm mb-2">Total Contracts</p>
          <p className="text-3xl font-bold text-foreground">{stats?.total_contracts || 0}</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm mb-2">Recent Uploads</p>
          <p className="text-3xl font-bold text-foreground">{stats?.recent_uploads || 0}</p>
          <p className="text-muted-foreground text-xs mt-1">Last {days} days</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm mb-2">OCR Success Rate</p>
          <p className="text-3xl font-bold text-green-400">{stats?.ocr_success_rate || 0}%</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm mb-2">OCR Failed</p>
          <p className="text-3xl font-bold text-red-400">{stats?.ocr_failed || 0}</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm mb-2">Avg Pages</p>
          <p className="text-3xl font-bold text-foreground">{stats?.avg_pages_per_contract || 0}</p>
        </div>
      </div>

      {/* Upload Volume Chart */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Upload Volume Trend</h2>
        <div className="flex items-end justify-between gap-1 h-48">
          {uploadVolume?.timeline?.map((day, idx) => {
            const height = (day.uploads / maxUploads) * 100;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center justify-end group">
                <div className="relative w-full">
                  <div
                    className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all hover:from-blue-400 hover:to-blue-300"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  />
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-muted px-2 py-1 rounded text-xs text-foreground whitespace-nowrap">
                    {day.uploads} uploads
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-muted-foreground text-sm mt-4">Total: {uploadVolume?.total_uploads || 0} uploads</p>
      </div>

      {/* Recent Contracts & Problems */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Ingestions */}
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Recent Ingestions</h2>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {recentContracts.map((contract) => (
              <div key={contract.id} className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-foreground font-medium">{contract.name}</p>
                  <span className="px-2 py-1 bg-blue-600 text-foreground text-xs rounded capitalize">
                    {contract.upload_type}
                  </span>
                </div>
                <p className="text-muted-foreground text-xs mb-1">{contract.user_name}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{contract.chunk_count} chunks</span>
                  <span>{new Date(contract.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Problem Documents */}
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">Problem Documents</h2>
            <span className="px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-red-400 text-sm">
              {problems?.total_problems || 0} issues
            </span>
          </div>
          
          <div className="space-y-4">
            {/* Failed OCR */}
            <div>
              <h3 className="text-foreground font-medium mb-2">Failed OCR Jobs</h3>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-400 font-bold text-2xl">{problems?.failed_ocr_jobs?.length || 0}</p>
                <p className="text-muted-foreground text-xs">OCR processing failures</p>
              </div>
            </div>

            {/* Low Chunk Contracts */}
            <div>
              <h3 className="text-foreground font-medium mb-2">Low Extraction Quality</h3>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
                <p className="text-orange-400 font-bold text-2xl">{problems?.low_chunk_contracts?.length || 0}</p>
                <p className="text-muted-foreground text-xs">Contracts with &lt;5 chunks</p>
              </div>
            </div>

            {/* Non-Labor Documents */}
            <div>
              <h3 className="text-foreground font-medium mb-2">Non-Labor Documents</h3>
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                <p className="text-yellow-400 font-bold text-2xl">{problems?.non_labor_documents?.length || 0}</p>
                <p className="text-muted-foreground text-xs">Flagged as non-CBA content</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Type Breakdown */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Upload Type Distribution</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {stats?.upload_breakdown && Object.entries(stats.upload_breakdown).map(([type, count]) => (
            <div key={type} className="bg-muted/50 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-foreground">{count}</p>
              <p className="text-muted-foreground text-sm capitalize mt-1">{type}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
